import { Injectable } from '@angular/core';
import { ASSET_TYPES } from '../constants/asset.constants';
import {
  OFFBOARDING_AUDIT_TARGET_SEPARATOR,
  OFFBOARDING_CHECKLIST_CARD_ID_PREFIX,
  OFFBOARDING_CHECKLIST_REVOKED_STATUS,
  OFFBOARDING_DECLINABLE_TOOL_ACCESS_STATUSES,
  OFFBOARDING_GRANTED_OUTSIDE_STESSA_HINT,
  OFFBOARDING_NO_ACCESS_FOUND_SCOPE,
  OFFBOARDING_PROOFHUB_ACCESS_LEVEL_LABEL,
  OFFBOARDING_PROVIDERS,
  OFFBOARDING_RECORD_CARD_ID_PREFIX,
  OFFBOARDING_RESOURCE_TYPE_LABEL_MAP,
  OFFBOARDING_REVIEW_ACCESS_PROVIDER_SLUGS,
  OFFBOARDING_REVOKABLE_LIVE_STATUSES,
  OFFBOARDING_SECTION_IDS,
  OFFBOARDING_SECTION_LABELS,
  OFFBOARDING_STATUS_PILL,
  OFFBOARDING_TERMINAL_TOOL_ACCESS_STATUSES,
  OFFBOARDING_TOOL_METHOD_LABEL,
  OFFBOARDING_UNVERIFIED_ACCESS_FALLBACK_WARNING,
} from '../constants/offboarding.constants';
import {
  IAccessComparisonRow,
  IOffboardingAsset,
  IOffboardingAssetCard,
  IOffboardingAssetRow,
  IOffboardingAuditLogEntry,
  IOffboardingAuditRevokeMetadata,
  IOffboardingCardBuildState,
  IOffboardingChecklistItem,
  IOffboardingManualToolAccessRow,
  IOffboardingOverview,
  IOffboardingRemovedAccess,
  IOffboardingReviewAccessItem,
  IOffboardingStatusPill,
  IOffboardingToolIdentity,
  IOffboardingSummaryBuildState,
  IOffboardingSummaryCard,
  IOffboardingToolAccessCard,
  OffboardingSummaryCardType,
  OffboardingToolAccessFilter,
} from '../interfaces/offboarding.interface';
import { assetTypeIconUrl } from '../utils/asset-icon.util';
import { toolLogoUrl } from '../utils/tool-display.util';

@Injectable({ providedIn: 'root' })
export class OffboardingCardBuilderService {
  buildToolAccessCards(overview: IOffboardingOverview, state: IOffboardingCardBuildState): IOffboardingToolAccessCard[] {
    const cards: IOffboardingToolAccessCard[] = [];
    const automatedToolAccess = overview.automated ?? [];
    const linkedProviderSlugs = new Set(
      automatedToolAccess.filter((toolAccess) => !toolAccess.needsIdentity).map((toolAccess) => toolAccess.tool),
    );
    const manualRecordIds = new Set((overview.manual ?? []).map((record) => record.toolAccessId));
    const removedItems = [...state.removedAccessItems.values()];
    const emailMatchedRequestSlugs = OFFBOARDING_PROVIDERS.filter(
      (provider) =>
        !provider.supportsManualLink &&
        OFFBOARDING_REVIEW_ACCESS_PROVIDER_SLUGS.includes(provider.slug) &&
        this.pendingReviewRequests(overview, provider.slug).length > 0,
    ).map((provider) => provider.slug);
    const providerSlugs = [...new Set([...linkedProviderSlugs, ...removedItems.map((item) => item.providerSlug), ...emailMatchedRequestSlugs])];
    const reviewCardProviderSlugs = new Set<string>();

    for (const providerSlug of providerSlugs) {
      const toolAccess = automatedToolAccess.find((candidate) => candidate.tool === providerSlug);
      const liveRows = toolAccess?.rows ?? [];
      const warning = toolAccess?.verified === false ? toolAccess.warning || OFFBOARDING_UNVERIFIED_ACCESS_FALLBACK_WARNING : undefined;
      const identity = toolAccess?.identity ?? null;
      if (OFFBOARDING_REVIEW_ACCESS_PROVIDER_SLUGS.includes(providerSlug)) {
        const pendingRequests = this.pendingReviewRequests(overview, providerSlug);
        const reviewCard = this.buildReviewAccessCard(providerSlug, liveRows, pendingRequests, state, toolAccess?.withAccountRows ?? []);
        if (reviewCard) {
          cards.push({ ...reviewCard, warning });
          reviewCardProviderSlugs.add(providerSlug);
        } else if (toolAccess?.noAccessFound) cards.push(this.buildNoAccessFoundCard(providerSlug, identity, state));
        continue;
      }
      const toolName = this.providerName(providerSlug);
      const liveCardIds = new Set<string>();
      for (const liveAccess of liveRows) {
        const isRevokable = OFFBOARDING_REVOKABLE_LIVE_STATUSES.includes(liveAccess.status);
        const needsStessaUpdate = liveAccess.status === 'REVOKED_EXTERNALLY' && !!liveAccess.toolAccessId;
        const cardId = this.liveAccessId(providerSlug, liveAccess);
        liveCardIds.add(cardId);
        const isRevoking = isRevokable && state.revokingCardIds.has(cardId);
        const failReason = isRevokable && !isRevoking ? state.failedRevokeReasons.get(cardId) : undefined;
        cards.push({
          id: cardId,
          toolName,
          logoUrl: toolLogoUrl(toolName),
          detail: this.resourceLabel(liveAccess),
          category: this.toolCategory(toolName, state),
          isAutomatic: true,
          resourceLabel: this.cardResourceLabel(liveAccess),
          status: isRevoking
            ? OFFBOARDING_STATUS_PILL.REVOKING
            : failReason
              ? OFFBOARDING_STATUS_PILL.FAILED
              : isRevokable
                ? OFFBOARDING_STATUS_PILL.ACTIVE
                : needsStessaUpdate
                  ? OFFBOARDING_STATUS_PILL.REMOVED_EXTERNALLY
                  : OFFBOARDING_STATUS_PILL.REVOKED,
          isRevoking,
          failReason,
          hint:
            liveAccess.status === 'ACTIVE_NOT_TRACKED'
              ? OFFBOARDING_GRANTED_OUTSIDE_STESSA_HINT
              : identity
                ? `${identity.handleLabel}: ${identity.handle}`
                : '',
          action: isRevokable ? 'revoke' : needsStessaUpdate ? 'markRevokedInStessa' : 'none',
          isCompleted: !isRevokable && !needsStessaUpdate,
          revokedAt: !isRevokable && !needsStessaUpdate ? state.revokedAtByCardId.get(cardId) : undefined,
          providerSlug,
          liveAccess,
          warning,
        });
      }
      for (const removedItem of removedItems) {
        const recordId = removedItem.liveAccess.toolAccessId;
        if (removedItem.providerSlug !== providerSlug || liveCardIds.has(removedItem.id)) continue;
        if (recordId && manualRecordIds.has(recordId)) continue;
        cards.push(this.buildRevokedAccessCard(removedItem, state));
      }
    }

    const reviewedRecordIds = new Set(
      removedItems
        .filter((item) => OFFBOARDING_REVIEW_ACCESS_PROVIDER_SLUGS.includes(item.providerSlug))
        .map((item) => item.liveAccess.toolAccessId)
        .filter((recordId): recordId is string => !!recordId),
    );
    const employeeName = this.employeeName(state);
    const promptedProviderSlugs = new Set<string>();
    const noAccountConfirmedSlugs = new Set<string>();
    for (const checklistItem of overview.checklist ?? []) {
      const provider = OFFBOARDING_PROVIDERS.find((candidate) => candidate.slug === checklistItem.providerSlug);
      if (!provider) continue;
      const isNoAccountConfirmed = checklistItem.status === OFFBOARDING_CHECKLIST_REVOKED_STATUS;
      if (isNoAccountConfirmed) noAccountConfirmedSlugs.add(provider.slug);
      else promptedProviderSlugs.add(provider.slug);
      const toolName = this.providerName(provider.slug);
      cards.push({
        id: `link:${provider.slug}`,
        checklistItem,
        toolName,
        logoUrl: toolLogoUrl(toolName),
        detail: `${provider.handleLabel} not linked`,
        status: isNoAccountConfirmed ? OFFBOARDING_STATUS_PILL.NO_ACCOUNT : OFFBOARDING_STATUS_PILL.ACCOUNT_NOT_LINKED,
        hint: isNoAccountConfirmed
          ? `Confirmed: ${employeeName} has no ${toolName} account.`
          : `Enter their ${provider.handleLabel} to check for access that was never requested through Stessa.`,
        action: isNoAccountConfirmed ? 'none' : 'linkAccount',
        canConfirmNoAccount: !isNoAccountConfirmed,
        isCompleted: isNoAccountConfirmed,
        revokedAt: isNoAccountConfirmed ? (checklistItem.updatedAt ?? undefined) : undefined,
        providerSlug: provider.slug,
        accountLabel: provider.handleLabel,
        category: this.toolCategory(toolName, state),
        isAutomatic: true,
      });
    }
    for (const stessaRecord of overview.manual ?? []) {
      if (reviewedRecordIds.has(stessaRecord.toolAccessId)) continue;
      const subCategory = stessaRecord.subCategory ?? '';
      const toolName = stessaRecord.toolName || subCategory || 'Tool access';
      const provider = OFFBOARDING_PROVIDERS.find((candidate) => candidate.subCategory.toLowerCase() === subCategory.toLowerCase());
      const isTerminal = OFFBOARDING_TERMINAL_TOOL_ACCESS_STATUSES.includes(stessaRecord.status);
      const isRequestPending = OFFBOARDING_DECLINABLE_TOOL_ACCESS_STATUSES.includes(stessaRecord.status);
      const hasNoLiveIdentity = !!provider && !linkedProviderSlugs.has(provider.slug) && !isTerminal;
      // Once "no account" is confirmed, the record is resolved on its own (decline / mark revoked), not by linking.
      const needsAccountLink = hasNoLiveIdentity && provider.supportsManualLink && !noAccountConfirmedSlugs.has(provider.slug);
      const isAccountMissing = hasNoLiveIdentity && !provider.supportsManualLink && !isRequestPending;
      const isFoldedIntoReviewCard = !!provider && isRequestPending && reviewCardProviderSlugs.has(provider.slug);
      if (isFoldedIntoReviewCard) continue;
      if (needsAccountLink && promptedProviderSlugs.has(provider.slug)) continue;
      if (needsAccountLink) promptedProviderSlugs.add(provider.slug);
      const canMarkRevoked = !needsAccountLink && !isTerminal && !isRequestPending;
      const cardId = `${OFFBOARDING_RECORD_CARD_ID_PREFIX}${stessaRecord.toolAccessId}`;
      cards.push({
        id: cardId,
        toolName,
        logoUrl: toolLogoUrl(subCategory || toolName),
        detail: needsAccountLink ? `${provider!.handleLabel} not linked` : isAccountMissing ? 'No account found' : 'Manual removal',
        status: isTerminal
          ? this.terminalStatus(stessaRecord.status)
          : needsAccountLink
            ? OFFBOARDING_STATUS_PILL.ACCOUNT_NOT_LINKED
            : isRequestPending
              ? OFFBOARDING_STATUS_PILL.REQUEST_PENDING
              : isAccountMissing
                ? OFFBOARDING_STATUS_PILL.REMOVED_EXTERNALLY
                : OFFBOARDING_STATUS_PILL.MANUAL_REMOVAL_REQUIRED,
        hint: isAccountMissing
          ? `No ${toolName} account matches ${state.employee.email}. Mark it as revoked to close the record.`
          : '',
        action: needsAccountLink
          ? 'linkAccount'
          : isRequestPending
            ? 'declineRequest'
            : canMarkRevoked
              ? 'markRevokedManually'
              : 'none',
        isCompleted: isTerminal,
        revokedAt: isTerminal ? state.revokedAtByCardId.get(cardId) : undefined,
        providerSlug: provider?.slug,
        accountLabel: provider?.handleLabel,
        stessaRecord,
        category: this.toolCategory(subCategory || toolName, state),
        isAutomatic: !!provider,
      });
    }

    for (const checklistItem of overview.checklist ?? []) {
      if (checklistItem.providerSlug) continue;
      cards.push(this.buildChecklistCard(checklistItem, state));
    }
    return cards;
  }

  buildSummaryCards(state: IOffboardingSummaryBuildState): IOffboardingSummaryCard[] {
    const listedCardIds = new Set(state.toolAccessCards.map((card) => card.id));
    const resolvedToolAccessCount = [...state.resolvedToolAccessCardIds].filter((cardId) => !listedCardIds.has(cardId)).length;
    const toolAccessDone = this.completedCards(state.toolAccessCards).length + resolvedToolAccessCount;
    const toolAccessTotal = state.toolAccessCards.length + resolvedToolAccessCount;
    const pendingAllocationIds = new Set(state.pendingAssets.map((assetRow) => assetRow.assetAllocationId));
    const checkedInCount = [...state.checkedInAssetAllocationIds].filter((allocationId) => !pendingAllocationIds.has(allocationId)).length;
    const openNotesCount = state.recoveryNotes.filter((note) => note.status !== 'SETTLED').length;

    const recoveryNotesCard = this.summaryCard(
      'recoveryNotes',
      OFFBOARDING_SECTION_LABELS.recoveryNotes,
      OFFBOARDING_SECTION_IDS.recoveryNotes,
      state.recoveryNotes.length - openNotesCount,
      state.recoveryNotes.length,
    );
    const isRecoveryNotesStepDone = !openNotesCount || state.isFinanceConfirmed;
    if (recoveryNotesCard.hasItems && isRecoveryNotesStepDone) {
      recoveryNotesCard.isComplete = true;
      recoveryNotesCard.progressPercent = 100;
    }

    return [
      recoveryNotesCard,
      this.summaryCard(
        'toolAccess',
        OFFBOARDING_SECTION_LABELS.toolAccess,
        OFFBOARDING_SECTION_IDS.toolAccess,
        toolAccessDone,
        toolAccessTotal,
      ),
      this.summaryCard(
        'assets',
        OFFBOARDING_SECTION_LABELS.assets,
        OFFBOARDING_SECTION_IDS.assets,
        checkedInCount,
        state.pendingAssets.length + checkedInCount,
      ),
    ];
  }

  buildAssetCards(assetRows: IOffboardingAssetRow[]): IOffboardingAssetCard[] {
    return assetRows.map((assetRow) => ({
      assetRow,
      typeName: this.assetTypeName(assetRow.asset),
      iconUrl: this.assetIconUrl(assetRow.asset),
    }));
  }

  assetTypeName(asset: IOffboardingAsset): string {
    const knownType = ASSET_TYPES.find((assetType) => assetType.assetId === asset.type);
    if (knownType) return knownType.assetName;
    const name = (asset.name ?? '').trim();
    const modelName = (asset.modelName ?? '').trim();
    if (!modelName || !name.toLowerCase().endsWith(modelName.toLowerCase())) return '';
    return name.slice(0, name.length - modelName.length).trim();
  }

  assetIconUrl(asset: IOffboardingAsset): string | null {
    return assetTypeIconUrl(this.assetTypeName(asset));
  }

  /** A finished access item, as the backend reports it, shown as removed in Review access. */
  removedAccessItem(removed: IOffboardingRemovedAccess): IOffboardingReviewAccessItem {
    const isAlreadyRemoved = removed.outcome === 'ALREADY_REMOVED';
    const liveAccess: IAccessComparisonRow = {
      resourceType: removed.resourceType,
      resourceKey: removed.resourceKey,
      role: null,
      status: !removed.toolAccessId ? 'ACTIVE_NOT_TRACKED' : isAlreadyRemoved ? 'REVOKED_EXTERNALLY' : 'ACTIVE',
      toolAccessId: removed.toolAccessId,
      ...(removed.name && { name: removed.name }),
    };
    return { ...this.toReviewAccessItem(removed.providerSlug, liveAccess), state: isAlreadyRemoved ? 'gone' : 'revoked' };
  }

  /** The name saved with the entry when the action was logged, in place of the resource key. */
  auditTargetName(entry: IOffboardingAuditLogEntry): string {
    const resource = ((entry.metadata ?? {}) as IOffboardingAuditRevokeMetadata).resource;
    if (!resource?.name) return entry.targetName;
    const providerSlug = (entry.targetName ?? '').split(OFFBOARDING_AUDIT_TARGET_SEPARATOR)[0];
    return `${providerSlug}${OFFBOARDING_AUDIT_TARGET_SEPARATOR}${resource.name}`;
  }

  toReviewAccessItem(providerSlug: string, liveAccess: IAccessComparisonRow): IOffboardingReviewAccessItem {
    return {
      id: this.liveAccessId(providerSlug, liveAccess),
      providerSlug,
      liveAccess,
      name: this.reviewItemName(liveAccess),
      source: liveAccess.status === 'ACTIVE_NOT_TRACKED' ? 'external' : liveAccess.status === 'REVOKED_EXTERNALLY' ? 'stessa' : 'both',
      state: 'pending',
    };
  }

  liveAccessId(providerSlug: string, liveAccess: IAccessComparisonRow): string {
    return `${providerSlug}:${liveAccess.resourceType}:${liveAccess.resourceKey}`;
  }

  checklistCardId(checklistItem: IOffboardingChecklistItem): string {
    return `${OFFBOARDING_CHECKLIST_CARD_ID_PREFIX}${checklistItem.itemKey}`;
  }

  providerName(providerSlug: string): string {
    return OFFBOARDING_PROVIDERS.find((candidate) => candidate.slug === providerSlug)?.subCategory ?? providerSlug;
  }

  toolSubtitle(card: IOffboardingToolAccessCard): string {
    const base = card.category ?? (card.isAutomatic ? OFFBOARDING_TOOL_METHOD_LABEL.automatic : OFFBOARDING_TOOL_METHOD_LABEL.manual);
    return card.resourceLabel ? `${base} · ${card.resourceLabel}` : base;
  }

  pendingCards(cards: IOffboardingToolAccessCard[]): IOffboardingToolAccessCard[] {
    return cards.filter((card) => !card.isCompleted);
  }

  completedCards(cards: IOffboardingToolAccessCard[]): IOffboardingToolAccessCard[] {
    return cards.filter((card) => card.isCompleted);
  }

  effectiveFilter(cards: IOffboardingToolAccessCard[], filter: OffboardingToolAccessFilter, isFilterChosen = false): OffboardingToolAccessFilter {
    if (isFilterChosen) return filter;
    return filter === 'pending' && !this.pendingCards(cards).length ? 'all' : filter;
  }

  visibleCards(
    cards: IOffboardingToolAccessCard[],
    filter: OffboardingToolAccessFilter,
    actionFlashes: ReadonlyMap<string, string>,
    isFilterChosen = false,
  ): IOffboardingToolAccessCard[] {
    const effectiveFilter = this.effectiveFilter(cards, filter, isFilterChosen);
    if (effectiveFilter === 'pending') return cards.filter((card) => !card.isCompleted || actionFlashes.has(card.id));
    if (effectiveFilter === 'completed') return this.completedCards(cards);
    return [...this.pendingCards(cards), ...this.completedCards(cards)];
  }

  isRevokedCard(card: IOffboardingToolAccessCard): boolean {
    return card.isCompleted && card.status === OFFBOARDING_STATUS_PILL.REVOKED;
  }

  private buildChecklistCard(checklistItem: IOffboardingChecklistItem, state: IOffboardingCardBuildState): IOffboardingToolAccessCard {
    const cardId = this.checklistCardId(checklistItem);
    const isRevoked = checklistItem.status === OFFBOARDING_CHECKLIST_REVOKED_STATUS;
    return {
      id: cardId,
      toolName: checklistItem.name,
      logoUrl: toolLogoUrl(checklistItem.name),
      detail: OFFBOARDING_TOOL_METHOD_LABEL.manual,
      status: isRevoked ? OFFBOARDING_STATUS_PILL.REVOKED : OFFBOARDING_STATUS_PILL.MANUAL_REMOVAL_REQUIRED,
      hint: '',
      action: isRevoked ? 'none' : 'markRevokedManually',
      isCompleted: isRevoked,
      revokedAt: isRevoked ? (checklistItem.updatedAt ?? undefined) : undefined,
      category: checklistItem.category || this.toolCategory(checklistItem.name, state),
      isAutomatic: false,
      checklistItem,
    };
  }

  private pendingReviewRequests(overview: IOffboardingOverview, providerSlug: string): IOffboardingManualToolAccessRow[] {
    const subCategory = OFFBOARDING_PROVIDERS.find((candidate) => candidate.slug === providerSlug)?.subCategory.toLowerCase();
    return (overview.manual ?? []).filter(
      (record) =>
        OFFBOARDING_DECLINABLE_TOOL_ACCESS_STATUSES.includes(record.status) && (record.subCategory ?? '').toLowerCase() === subCategory,
    );
  }

  private buildReviewAccessCard(
    providerSlug: string,
    liveRows: IAccessComparisonRow[],
    pendingRequests: IOffboardingManualToolAccessRow[],
    state: IOffboardingCardBuildState,
    withAccountRows: IAccessComparisonRow[] = [],
  ): IOffboardingToolAccessCard | null {
    const liveItems = liveRows
      .filter((row) => OFFBOARDING_REVOKABLE_LIVE_STATUSES.includes(row.status) || (row.status === 'REVOKED_EXTERNALLY' && !!row.toolAccessId))
      .map((row): IOffboardingReviewAccessItem => {
        const item = this.toReviewAccessItem(providerSlug, row);
        const isRevoking = state.revokingCardIds.has(item.id);
        const failReason = isRevoking ? undefined : state.failedRevokeReasons.get(item.id);
        return { ...item, state: isRevoking ? 'revoking' : failReason ? 'failed' : 'pending', failReason };
      });
    const liveItemIds = new Set(liveItems.map((item) => item.id));
    const removedItems = [...state.removedAccessItems.values()]
      .filter((item) => item.providerSlug === providerSlug && !liveItemIds.has(item.id));
    const reviewItems = [...liveItems, ...removedItems];
    if (!reviewItems.length && !pendingRequests.length) return null;

    const provider = OFFBOARDING_PROVIDERS.find((candidate) => candidate.slug === providerSlug);
    const toolName = this.providerName(providerSlug);
    const isRevoking = liveItems.some((item) => item.state === 'revoking');
    const failReason = isRevoking ? undefined : liveItems.find((item) => item.failReason)?.failReason;
    const hasLiveAccess = liveItems.some((item) => OFFBOARDING_REVOKABLE_LIVE_STATUSES.includes(item.liveAccess.status));
    const remainingCount = liveItems.length;
    const pendingCount = pendingRequests.length;
    const pendingHint = `${pendingCount} pending request${pendingCount === 1 ? '' : 's'}`;
    const removedHint = remainingCount ? `${removedItems.length} of ${reviewItems.length} access items removed` : '';
    return {
      id: `review:${providerSlug}`,
      toolName,
      logoUrl: toolLogoUrl(toolName),
      detail: `${remainingCount} access item${remainingCount === 1 ? '' : 's'}`,
      status: isRevoking
        ? OFFBOARDING_STATUS_PILL.REVOKING
        : failReason
          ? OFFBOARDING_STATUS_PILL.FAILED
          : hasLiveAccess
            ? OFFBOARDING_STATUS_PILL.ACTIVE
            : remainingCount
              ? OFFBOARDING_STATUS_PILL.REMOVED_EXTERNALLY
              : pendingCount
                ? OFFBOARDING_STATUS_PILL.REQUEST_PENDING
                : OFFBOARDING_STATUS_PILL.REVOKED,
      isRevoking,
      failReason,
      hint: [removedHint, pendingCount ? pendingHint : ''].filter(Boolean).join(' · '),
      action: remainingCount ? 'revoke' : pendingCount ? 'reviewRequests' : 'none',
      isCompleted: !remainingCount && !pendingCount,
      revokedAt: remainingCount || pendingCount ? undefined : this.latestRevokedAt(removedItems.map((item) => item.id), state),
      providerSlug,
      accountLabel: provider?.handleLabel,
      category: this.toolCategory(toolName, state),
      isAutomatic: true,
      reviewItems,
      withAccountItems: withAccountRows.map((row) => ({ ...this.toReviewAccessItem(providerSlug, row), withAccount: true })),
      pendingRequests,
      identity: state.identityByProviderSlug.get(providerSlug),
    };
  }

  private buildNoAccessFoundCard(
    providerSlug: string,
    identity: IOffboardingToolIdentity | null,
    state: IOffboardingCardBuildState,
  ): IOffboardingToolAccessCard {
    const toolName = this.providerName(providerSlug);
    const account = identity ? `${identity.handleLabel} ${identity.handle}` : `${toolName} account`;
    return {
      id: `noaccess:${providerSlug}`,
      toolName,
      logoUrl: toolLogoUrl(toolName),
      detail: 'No access found',
      status: OFFBOARDING_STATUS_PILL.NO_ACCESS_FOUND,
      hint: `${account} is linked but has no access to ${OFFBOARDING_NO_ACCESS_FOUND_SCOPE[providerSlug] ?? `any ${toolName} resource`}. Nothing to revoke.`,
      action: 'none',
      isCompleted: true,
      providerSlug,
      category: this.toolCategory(toolName, state),
      isAutomatic: true,
      identity: identity ?? undefined,
    };
  }

  private buildRevokedAccessCard(item: IOffboardingReviewAccessItem, state: IOffboardingCardBuildState): IOffboardingToolAccessCard {
    const toolName = this.providerName(item.providerSlug);
    return {
      id: item.id,
      toolName,
      logoUrl: toolLogoUrl(toolName),
      detail: this.resourceLabel(item.liveAccess),
      status: OFFBOARDING_STATUS_PILL.REVOKED,
      hint: '',
      action: 'none',
      isCompleted: true,
      revokedAt: state.revokedAtByCardId.get(item.id),
      providerSlug: item.providerSlug,
      liveAccess: item.liveAccess,
      category: this.toolCategory(toolName, state),
      isAutomatic: true,
      resourceLabel: this.cardResourceLabel(item.liveAccess),
    };
  }

  private summaryCard(
    type: OffboardingSummaryCardType,
    label: string,
    sectionId: string,
    completedCount: number,
    totalCount: number,
  ): IOffboardingSummaryCard {
    return {
      type,
      label,
      sectionId,
      completedCount,
      totalCount,
      hasItems: totalCount > 0,
      isComplete: totalCount > 0 && completedCount === totalCount,
      progressPercent: totalCount ? (completedCount / totalCount) * 100 : 0,
    };
  }

  private latestRevokedAt(cardIds: string[], state: IOffboardingCardBuildState): string | undefined {
    return cardIds
      .map((cardId) => state.revokedAtByCardId.get(cardId))
      .filter((revokedAt): revokedAt is string => !!revokedAt)
      .sort((first, second) => new Date(second).getTime() - new Date(first).getTime())[0];
  }

  private employeeName(state: IOffboardingCardBuildState): string {
    return `${state.employee.firstName} ${state.employee.lastName}`.trim();
  }

  private toolCategory(toolName: string, state: IOffboardingCardBuildState): string | undefined {
    return state.categoryBySubCategory.get(toolName.trim().toLowerCase());
  }

  private reviewItemName(liveAccess: IAccessComparisonRow): string {
    if (liveAccess.name) return liveAccess.name;
    const isProjectTitle =
      liveAccess.resourceType === 'PROJECT' && !!liveAccess.role && liveAccess.role !== OFFBOARDING_PROOFHUB_ACCESS_LEVEL_LABEL;
    return isProjectTitle ? liveAccess.role! : liveAccess.resourceKey;
  }

  private cardResourceLabel(liveAccess: IAccessComparisonRow): string | undefined {
    return liveAccess.resourceType === 'ACCOUNT' ? undefined : this.resourceLabel(liveAccess);
  }

  private resourceLabel(liveAccess: IAccessComparisonRow): string {
    const resourceTypeLabel = OFFBOARDING_RESOURCE_TYPE_LABEL_MAP[liveAccess.resourceType] ?? liveAccess.resourceType;
    const showResourceKey =
      liveAccess.resourceKey && liveAccess.resourceType !== 'ORG' && liveAccess.resourceType !== 'ACCOUNT';
    const role = liveAccess.role ? ` · ${liveAccess.role}` : '';
    return `${resourceTypeLabel}${showResourceKey ? `: ${liveAccess.resourceKey}` : ''}${role}`;
  }

  private terminalStatus(status: string): IOffboardingStatusPill {
    if (status === 'DECLINED') return OFFBOARDING_STATUS_PILL.DECLINED;
    if (status === 'EXPIRED') return OFFBOARDING_STATUS_PILL.EXPIRED;
    return OFFBOARDING_STATUS_PILL.REVOKED;
  }
}
